import React, { Suspense, lazy, useEffect, useState } from 'react'
import { Button, ButtonGroup, Dropdown, Table } from 'react-bootstrap'
import ReactPaginate from 'react-paginate'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import Swal from 'sweetalert2'
import { useImmer } from 'use-immer'
import DeleteIcon from '../../../components/AllSvgIcon/DeleteIcon'
import EditIcon from '../../../components/AllSvgIcon/EditIcon'
import HKBadge from '../../../components/Badges'
import Loader from '../../../components/Loader'
import SearchBox from '../../../components/Searchbox'
import CustomToast from '../../../components/Toast/CustomToast'
import { customStyles } from '../../../components/customStyles'
import {
  calculatePageRange,
  encodedSearchText,
  showToast
} from '../../../lib/AllGlobalFunction'
import {
  allCrudNames,
  allPages,
  checkPageAccess
} from '../../../lib/AllPageNames'
import { pageRangeDisplayed } from '../../../lib/AllStaticVariables'
import axiosProvider from '../../../lib/AxiosProvider'
import { _SwalDelete, _exception } from '../../../lib/exceptionMessage'
import useDebounce from '../../../lib/useDebounce'
import DeliveryBulkUpload from './DeliveryBulkUpload'

const DeliveryForm = lazy(() => import('./DeliveryForm'))

const DeliveryList = () => {
  const initVal = {
    locality: '',
    countryID: '',
    stateID: '',
    cityID: '',
    pincode: null,
    deliveryDays: '',
    status: '',
    isCODActive: ''
  }
  const [searchText, setSearchText] = useState()
  const [data, setData] = useState()
  const [modalShow, setModalShow] = useState({ show: false, type: '' })
  const debounceSearchText = useDebounce(searchText, 500)
  const [initialValues, setInitialValues] = useState(initVal)
  const [loading, setLoading] = useState(false)
  const { userInfo, pageAccess } = useSelector((state) => state?.user)
  const location = useLocation()
  const [allState, setAllState] = useImmer({
    country: [],
    state: [],
    city: []
  })
  const [filterDetails, setFilterDetails] = useImmer({
    pageSize: 50,
    pageIndex: 1,
    searchText: ''
  })
  const [toast, setToast] = useState({
    show: false,
    text: null,
    variation: null
  })

  const handlePageClick = (event) => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    setFilterDetails((draft) => {
      draft.pageIndex = event.selected + 1
    })
  }

  const fetchData = async () => {
    try {
      setLoading(true)

      const response = await axiosProvider({
        method: 'GET',
        endpoint: 'Delivery/search',
        queryString: `?searchText=${encodedSearchText(
          filterDetails?.searchText
        )}&pageIndex=${filterDetails?.pageIndex}&pageSize=${
          filterDetails?.pageSize
        }`
      })
      setLoading(false)
      if (response?.status === 200) {
        setData(response)
      }
    } catch {
      setLoading(false)
    }
  }

  const handleDelete = async (id) => {
    try {
      setLoading(true)
      const response = await axiosProvider({
        method: 'DELETE',
        endpoint: 'Delivery',
        queryString: `?Id=${id}`,
        userId: userInfo?.userId,
        location: location.pathname
      })
      setLoading(false)
      if (response?.data?.code === 200) {
        fetchData()
      }
      showToast(toast, setToast, response)
    } catch {
      setLoading(false)

      showToast(toast, setToast, {
        data: {
          message: _exception?.message,
          code: 204
        }
      })
    }
  }

  const csv = async (countryId = null, stateId = null) => {
    if (!allState?.country?.length) {
      const response = await axiosProvider({
        method: 'GET',
        endpoint: 'Country/Search',
        queryString: `?pageindex=0&PageSize=0`
      })
      if (response?.status === 200) {
        setAllState((draft) => {
          draft.country = response?.data?.data
        })
      }
    }

    if (countryId) {
      const response = await axiosProvider({
        method: 'GET',
        endpoint: 'State/ByCountryId',
        queryString: `?id=${countryId}`
      })

      if (response?.status === 200) {
        setAllState((draft) => {
          draft.state = response?.data?.data
        })
      }
    }

    if (stateId) {
      const response = await axiosProvider({
        method: 'GET',
        endpoint: 'City/ByStateId',
        queryString: `?id=${stateId}`
      })

      if (response?.status === 200) {
        setAllState((draft) => {
          draft.city = response?.data?.data
        })
      }
    }
  }

  useEffect(() => {
    if (debounceSearchText) {
      setFilterDetails((draft) => {
        draft.searchText = debounceSearchText
        draft.pageIndex = 1
      })
    } else {
      setFilterDetails((draft) => {
        draft.searchText = ''
        draft.pageIndex = 1
      })
    }
  }, [debounceSearchText])

  useEffect(() => {
    fetchData()
  }, [filterDetails])

  return (
    <>
      <div className='d-flex align-items-center mb-3 gap-3 flex-row-reverse'>
        {checkPageAccess(
          pageAccess,
          allPages?.manageDelivery,
          allCrudNames?.write
        ) && (
          <Dropdown as={ButtonGroup}>
            <Button
              variant='warning'
              className='fw-600 d-flex align-items-center gap-2'
              onClick={() => {
                csv()
                setInitialValues(initVal)
                setModalShow({ type: 'form', show: true })
              }}
            >
              <i className='m-icon m-icon--plusblack'></i>
              Create
            </Button>
            <Dropdown.Toggle
              split
              variant='warning'
              id='dropdown-split-basic'
            />

            <Dropdown.Menu>
              <Dropdown.Item
                onClick={() =>
                  setModalShow({
                    show: true,
                    type: 'bulkUpload'
                  })
                }
              >
                <span>Bulk Upload</span>
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        )}

        <div className='page-range'>
          {calculatePageRange({
            ...filterDetails,
            recordCount: data?.data?.pagination?.recordCount ?? 0
          })}
        </div>

        <div className='d-flex align-items-center'>
          <label className='me-1'>Show</label>
          <select
            styles={customStyles}
            menuPortalTarget={document.body}
            name='dataget'
            value={filterDetails?.pageSize}
            id='parpageentries'
            className='form-select me-1'
            onChange={(e) => {
              setFilterDetails((draft) => {
                draft.pageSize = e?.target?.value
                draft.pageIndex = 1
              })
            }}
          >
            <option value='10'>10</option>
            <option value='25'>25</option>
            <option value='50'>50</option>
            <option value='100'>100</option>
            <option value='200'>200</option>
            <option value='500'>500</option>
          </select>
        </div>
        <SearchBox
          placeholderText={'Search'}
          value={searchText}
          searchclassnamewrapper={'searchbox-wrapper'}
          onChange={(e) => {
            setSearchText(e?.target?.value)
          }}
        />
      </div>

      <Table responsive className='align-middle table-list'>
        <thead>
          <tr>
            <th>Country</th>
            <th>State</th>
            <th>City</th>
            <th>Locality</th>
            <th>Pincode</th>
            <th>Delivery Days</th>
            <th>COD Available</th>
            <th>Status</th>
            {checkPageAccess(pageAccess, allPages?.manageDelivery, [
              allCrudNames?.update,
              allCrudNames?.delete
            ]) && <th className='text-center'>Action</th>}
          </tr>
        </thead>
        <tbody>
          {data?.data?.data?.length > 0 ? (
            data?.data?.data?.map((data, index) => (
              <tr key={data.id}>
                <td>{data.countryName}</td>
                <td>{data.stateName}</td>
                <td>{data.cityName}</td>
                <td>{data.locality}</td>
                <td>{data.pincode}</td>
                <td>{data.deliveryDays}</td>
                <td>{data.isCODActive ? 'Yes' : 'No'}</td>
                <td>
                  <HKBadge
                    badgesbgname={
                      data?.status?.toLowerCase() === 'active'
                        ? 'success'
                        : 'danger'
                    }
                    badgesTxtname={data.status}
                    badgeClassname={''}
                  />
                </td>
                {checkPageAccess(pageAccess, allPages?.manageDelivery, [
                  allCrudNames?.update,
                  allCrudNames?.delete
                ]) && (
                  <td className='text-center'>
                    <div className='d-flex gap-2 justify-content-center'>
                      {checkPageAccess(
                        pageAccess,
                        allPages?.manageDelivery,
                        allCrudNames?.update
                      ) && (
                        <span
                          onClick={() => {
                            setInitialValues(data)
                            setModalShow({ show: true, type: 'form' })
                            csv(data?.countryID, data?.stateID)
                          }}
                        >
                          <EditIcon bg={'bg'} />
                        </span>
                      )}
                      {checkPageAccess(
                        pageAccess,
                        allPages?.manageDelivery,
                        allCrudNames?.delete
                      ) && (
                        <span
                          onClick={() => {
                            Swal.fire({
                              title: _SwalDelete.title,
                              text: _SwalDelete.text,
                              icon: _SwalDelete.icon,
                              showCancelButton: _SwalDelete.showCancelButton,
                              confirmButtonColor:
                                _SwalDelete.confirmButtonColor,
                              cancelButtonColor: _SwalDelete.cancelButtonColor,
                              confirmButtonText: _SwalDelete.confirmButtonText,
                              cancelButtonText: _SwalDelete.cancelButtonText
                            }).then((result) => {
                              if (result.isConfirmed) {
                                handleDelete(data?.id)
                              } else if (result.isDenied) {
                              }
                            })
                          }}
                        >
                          <DeleteIcon bg={'bg'} />
                        </span>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={9} className='text-center'>
                {data?.data?.message}
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      {data?.data?.pagination?.pageCount > 0 && (
        <ReactPaginate
          className='list-inline m-cst--pagination d-flex justify-content-end gap-1'
          breakLabel='...'
          nextLabel=''
          onPageChange={handlePageClick}
          pageRangeDisplayed={pageRangeDisplayed}
          pageCount={data?.data?.pagination?.pageCount}
          previousLabel=''
          renderOnZeroPageCount={null}
          forcePage={filterDetails?.pageIndex - 1}
        />
      )}

      <Suspense fallback={<Loader />}>
        {modalShow?.show && modalShow?.type === 'form' && (
          <DeliveryForm
            loading={loading}
            setLoading={setLoading}
            initialValues={initialValues}
            fetchData={fetchData}
            modalShow={modalShow}
            setModalShow={setModalShow}
            toast={toast}
            setToast={setToast}
            allState={allState}
            setAllState={setAllState}
            csv={csv}
          />
        )}

        {modalShow?.show && modalShow?.type === 'bulkUpload' && (
          <DeliveryBulkUpload
            modalShow={modalShow}
            setModalShow={setModalShow}
            loading={loading}
            setLoading={setLoading}
            fetchData={fetchData}
            toast={toast}
            setToast={setToast}
          />
        )}
      </Suspense>

      {loading && !modalShow?.show && <Loader />}

      {toast?.show && !modalShow?.show && (
        <CustomToast text={toast?.text} variation={toast?.variation} />
      )}
    </>
  )
}

export default DeliveryList
